import * as pdfMake from "pdfmake/build/pdfmake.js";

import { InputBy, OutingGetModel, OutingType } from "@/commons/services/Outings";
import DateUtil from "@/plugins/date-util/DateUtil";

/**
 * ### 外出・外泊届けリマインドのPDF操作
 */
export class RemindOutingsPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "RemindReservationsPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      RemindOutingsPdf: {
        normal: "RemindReservationsPdf.ttf",
        bold: "RemindReservationsPdf.ttf"
      }
    };

    return;
  }

  /**
   * ### 外出・外泊届けリマインドPDFを作成してダウンロードさせる
   *
   * @param items 印刷対象の外出・外泊届けリマインド
   * @param facilityName 施設名
   * @param fileName ファイル名
   */
  public createAndDownloadPdf(items: Array<OutingGetModel>, facilityName: string, fileName: string): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [15, 14, 15, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 10,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "RemindOutingsPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 外出・外泊届けリマインド一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   *   - 作成日
   *   - タイトル
   *   - 施設名
   * - コンテンツ部(外出・外泊届けリマインド情報)
   */
  private createContent(items: Array<OutingGetModel>, facilityName: string): any {
    let contents: Array<any> = [];
    contents = this.createHeader(facilityName);
    contents.push(this.createTable(items));
    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   *
   * @param facilityName 施設名
   */
  private createHeader(facilityName: string): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: "HitomeQコネクト 本日の外出外泊届け一覧",
      style: "title"
    });

    // 施設名、利用者名
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 外出・外泊届けリマインド情報
   */
  private createTable(items: Array<OutingGetModel>): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        // 各列の幅指定、列数と数が合わないとエラーになるので注意する
        widths: [50, 50, 70, 80, 100, 120]
      }
    };
    const body: Array<any> = [
      [
        { text: "種別", style: "tableHeader" },
        { text: "入力", style: "tableHeader" },
        { text: "状態", style: "tableHeader" },
        { text: "日時", style: "tableHeader" },
        { text: "利用者名\n（部屋名）", style: "tableHeader" },
        { text: "申請者（続柄）\nお迎えに来る方（続柄）", style: "tableHeader" }
      ]
    ];
    for (const item of items) {
      const typeName: string = item.type == OutingType.GOING_OUT ? "外出" : item.type == OutingType.STAYING_OUT ? "外泊" : "";
      const inputBy: string = item.inputBy == InputBy.FAMILY ? "本人" : item.inputBy == InputBy.FACILITY ? "代理" : "";
      const status: string = item.deleteFlag == true ? "キャンセル済" : item.consented == false ? "未承諾" : "承諾";
      const datetime: string = this.getDatetime(item);
      const pickupPerson = item.applicantPickup ? "申請者と同じ" : `${item.pickupPerson?.name}（${item.pickupPerson?.relation}）`;
      body.push([
        { text: typeName, style: { alignment: "center" } },
        { text: inputBy, style: { alignment: "center" } },
        { text: status, style: { alignment: "center" } },
        datetime,
        `${item.targetResidentInfo.name}\n（${item.targetResidentInfo.roomName}）`,
        `${item.applicantFamilyInfo.name}（${item.applicantFamilyInfo.relation}）\n${pickupPerson}`
      ]);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  private getDatetime(item: OutingGetModel): string {
    if (item.type == OutingType.GOING_OUT) {
      const startDatetime = this.dateUtil.convertNumberToString(item.startDatetime, "yyyy/mm/dd hh:MM");
      const endDatetime = this.dateUtil.convertNumberToString(item.endDatetime, "hh:MM");
      return `${startDatetime} ～ ${endDatetime}`;
    } else if (item.type == OutingType.STAYING_OUT) {
      const startDatetime = this.dateUtil.convertNumberToString(item.startDatetime, "yyyy/mm/dd hh:MM");
      const endDatetime = this.dateUtil.convertNumberToString(item.endDatetime, "yyyy/mm/dd hh:MM");
      return `${startDatetime} ～\n ${endDatetime}`;
    }
    return "";
  }
}
